import React from "react";
import { Header as H1 } from "../../components/utils/Headings";
import Spinner from "../../components/utils/Spinner";
import { api } from "../../dependencies";
import { AnyAction, Dispatch } from "redux";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import { Creators as AllCreators } from "../../../../src/ts/actions";

const Creators = (AllCreators as any).App.SelectOrganization;

interface IUserContextState {
  usersIds: string[];
  deleteUser: (userId: string) => void;
  addNewUser: () => void;
}

export interface IUserContextComponentOwnProps {
  organizationId: string;
  accountId: string;
  isOrgAdmin: boolean;
}

interface IUserContextDispatchProps {
  onAddNewUserSuccess: (accountId: string) => void;
  reloadUsersAmount: () => void;
}

type IUserContextComponentProps = IUserContextComponentOwnProps & IUserContextDispatchProps;

export const UserContext = React.createContext<IUserContextState | undefined>(undefined);

export const useUserContext = () => {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

export const UserContextInnerComponent: React.FC<IUserContextComponentProps> = ({
  children,
  organizationId,
  accountId,
  isOrgAdmin,
  onAddNewUserSuccess,
  reloadUsersAmount,
}) => {
  const [usersIds, setUsersIds] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const getAllUsersIds = () => {
    setIsLoading(true);
    api.users
      .getEditableByRelativeTo(accountId, organizationId)
      .then(([err, res]) => {
        if (err || !res) {
          setIsError(true);
        }
        setUsersIds(res?.data ?? []);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  };

  const deleteUser = (userId: string) => {
    api.users
      .deleteUser(accountId, userId)
      .then(getAllUsersIds)
      .then(reloadUsersAmount)
      .catch(() => setIsError(true));
  };

  const addNewUser = () => {
    api.users
      .addNewUser(accountId, organizationId)
      .then(res => onAddNewUserSuccess(res.data.value))
      .then(reloadUsersAmount)
      .catch(() => setIsError(true));
  };

  React.useEffect(() => {
    if (!isOrgAdmin) return;
    getAllUsersIds();
  }, []);

  if (isLoading) return <Spinner />;

  if (!isOrgAdmin) return <div>Only organizations admins have access to this content.</div>;

  if (isError)
    return (
      <>
        <H1>Accounts</H1>
        <div>Fetching the accounts failed. Please, try again.</div>
      </>
    );

  return <UserContext.Provider value={{ usersIds, deleteUser, addNewUser }}>{children}</UserContext.Provider>;
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IUserContextDispatchProps => ({
  onAddNewUserSuccess: (accountId: string) => dispatch(push(`/account-management/${accountId}`)),
  reloadUsersAmount: () => dispatch(Creators.loadUsersAmount()),
});

export const UserContextComponent = connect(null, mapDispatchToProps)(UserContextInnerComponent);
